import { Anima } from "kido/anima";
import { NativeScroller } from "kido/native-scroller";
import { Sniff } from "kido/utils";

import { App, type GpuInstance } from "@app/context";
import GPU from "@app/gpu";
import { PageManager } from "@app/page-manager";

import Loader from "@engine/boot/loader";

type ReadyCallback = (next: () => void) => void;

export default class Intro {
  private barEl: HTMLElement | null;
  private _startTime: number;

  constructor(cb: ReadyCallback) {
    const introEl = document.querySelector(".intro") as HTMLElement | null;
    this.barEl = document.querySelector(".intro__bar");
    this._startTime = performance.now();

    const swipeUp = () => {
      if (!introEl) return;
      App._introSwipeResolve?.();
      App._introSwipeResolve = null;
      new Anima({
        el: introEl,
        p: { y: [0, -100, '%'] },
        d: 500,
        e: 'ioW',
        cb: () => {
          try {
            App.introDone = true;
            try {
              const gpu = App.gpu as GpuInstance;
              gpu?.renderer?.requestBoundsUpdate?.();
              gpu?.requestExtraFrames?.();
            } catch { }
          } catch { }
          introEl.remove();
        },
      }).play();
    };

    const onComplete = () => {
      const elapsed = performance.now() - this._startTime;
      const MIN_DISPLAY = 500;
      const remaining = Math.max(MIN_DISPLAY - elapsed, 0);

      // Animate bar to 100% over the remaining minimum display time
      // so the fill is always visible even when loading is instant
      const currentWidth = this.barEl
        ? parseFloat(this.barEl.style.width) || 0
        : 100;

      if (this.barEl && currentWidth < 100 && remaining > 0) {
        const bar = this.barEl;
        new Anima({
          d: remaining,
          e: 'oQ',
          u: (v: { prE: number }) => {
            bar.style.width = `${currentWidth + (100 - currentWidth) * v.prE}%`;
          },
          cb: () => this._collapseBar(swipeUp),
        }).play();
      } else {
        if (this.barEl) this.barEl.style.width = "100%";
        setTimeout(() => this._collapseBar(swipeUp), remaining);
      }
    };

    if (Sniff.isMobile) {
      // Mobile: skip GPU entirely — use native video/img
      console.debug("[mobile] skipping GPU, using native media");
      this.runMobileBar(() => {
        onComplete();
        this.initMobile();
      });
      return;
    }

    cb(async () => {
      console.debug("[gpu:init]");
      const gpu = new GPU();
      App.gpu = gpu;

      // Start fetching + decoding current-route images NOW,
      // in parallel with GPU adapter/device initialization.
      // By the time the device is ready, images are already decoded.
      const prefetched = Loader.prefetchCurrentRoute();

      // Initialize WebGPU (async — adapter + device + shader compile)
      await gpu.load();

      if (!gpu.device) {
        console.warn("[gpu:fallback] WebGPU unavailable, using native media");
        App.gpu = null;
        gpu.destroy();
        const canvas = document.getElementById("canvas");
        if (canvas) canvas.remove();
        document.body.classList.remove("has-gpu");
        this.runMobileBar(() => {
          onComplete();
          this.initMobile();
        });
        return;
      }

      // Upload pre-decoded bitmaps to GPU + load remaining textures
      let desktopReadyFired = false;
      new Loader({
        prefetched,
        onProgress: (loaded, total) => {
          this.updateProgress(loaded, total);
          // Start home page animation at 50% progress
          if (!desktopReadyFired && total > 0 && loaded / total >= 0.5) {
            desktopReadyFired = true;
            this.onDesktopReady();
          }
        },
        onComplete: () => {
          if (!desktopReadyFired) {
            desktopReadyFired = true;
            this.onDesktopReady();
          }
          onComplete();
        },
      });
    });
  }

  private _collapseBar(cb: () => void): void {
    if (this.barEl) {
      this.barEl.style.transformOrigin = "right";
      const bar = this.barEl;
      new Anima({
        d: 400,
        e: 'ioW',
        u: (v: { prE: number }) => {
          bar.style.transform = `scaleX(${1 - v.prE})`;
        },
        cb,
      }).play();
    } else {
      cb();
    }
  }

  private runMobileBar(onDone: () => void): void {
    const duration = 500;
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const pct = Math.min((elapsed / duration) * 100, 100);
      if (this.barEl) {
        this.barEl.style.width = `${pct}%`;
        this.barEl.setAttribute("aria-valuenow", String(Math.round(pct)));
      }
      if (pct < 100) {
        requestAnimationFrame(tick);
      } else {
        onDone();
      }
    };
    requestAnimationFrame(tick);
  }

  private updateProgress(loaded: number, total: number): void {
    if (!this.barEl) return;
    const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
    this.barEl.style.width = `${percent}%`;
    this.barEl.setAttribute("aria-valuenow", String(percent));
  }

  async onDesktopReady(): Promise<void> {
    const gpu = App.gpu as GpuInstance;
    if (!gpu) return;

    gpu.intro!();
    gpu.init!();
    gpu.run!();

    console.debug("[gpu:ready]");

    try {
      console.debug("[scroller:init]");
      App.scroller = new NativeScroller({
        container: document.querySelector(".s__c") as HTMLElement,
        damping: 0.09,
        onUpdate: () => {
          // Request WebGPU redraw on scroll
          const g = App.gpu as GPU | null;
          if (g) {
            g.requestRedraw();
            g.requestBoundsUpdate();
          }
        },
      });

      // Register all routes from config
      const routes = Object.keys(App.config.routes);
      for (const route of routes) {
        App.scroller.registerRoute(route);
      }

      App.scroller.setActiveRoute(App.route.new.url ?? "/");
      App.scroller.start();

      // Set videosPlaying before page init so VTs start playing
      // immediately when they init (no pause-after-first-frame)
      gpu.videosPlaying = true;

      PageManager.registerFromRoutes?.();
      await PageManager.afterIn();
      gpu.setVideosPlaying?.(true);
    } catch { }
  }

  private async initMobile(): Promise<void> {
    try {
      console.debug("[scroller:init] mobile");
      App.scroller = new NativeScroller({
        container: document.querySelector(".s__c") as HTMLElement,
        damping: 0.09,
      });

      const routes = Object.keys(App.config.routes);
      for (const route of routes) {
        App.scroller.registerRoute(route);
      }

      App.scroller.setActiveRoute(App.route.new.url ?? "/");
      App.scroller.start();

      PageManager.registerFromRoutes?.();
      await PageManager.afterIn();
    } catch { }
  }
}
